// Глобальный ErrorBoundary + window-error-overlay.
//
// Замена white-screen-of-death при React-крахах и unhandled exceptions.
// Telegram Desktop НЕ имеет DevTools (Ctrl+Shift+I не работает), так что
// без on-screen overlay юзер видит только чёрный экран — диагностика
// невозможна. Этот компонент рисует красный экран с message + stack +
// componentStack + кнопками «Скопировать» и «Перезагрузить».
//
// Подключение в main.tsx:
//   <ErrorBoundary><App /></ErrorBoundary>
// + в main.tsx уже инсталлируются window.onerror / unhandledrejection
//   листенеры, которые тоже показывают overlay.

import React from 'react';

interface CrashInfo {
  message: string;
  stack?: string;
  componentStack?: string;
  source?: string;  // 'react' | 'window.onerror' | 'unhandledrejection'
}

interface State {
  crash: CrashInfo | null;
}

// Singleton ссылка на инстанс — чтобы window-handlers могли пушить ошибки.
let activeBoundary: ErrorBoundary | null = null;

export function reportGlobalCrash(info: CrashInfo): void {
  if (activeBoundary) activeBoundary.showCrash(info);
  else {
    // ErrorBoundary ещё не смонтирован — fallback в alert.
    try {
      console.error('[crash before boundary]', info);
      // eslint-disable-next-line no-alert
      alert(`[Корпы] Ранний краш:\n${info.message}\n\n${info.stack || ''}`);
    } catch {}
  }
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { crash: null };

  componentDidMount() {
    activeBoundary = this;
  }
  componentWillUnmount() {
    if (activeBoundary === this) activeBoundary = null;
  }

  static getDerivedStateFromError(err: unknown): State {
    return {
      crash: {
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
        source: 'react',
      },
    };
  }

  componentDidCatch(err: unknown, info: React.ErrorInfo) {
    this.setState({
      crash: {
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
        componentStack: info?.componentStack || undefined,
        source: 'react',
      },
    });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', err, info);
  }

  showCrash(crash: CrashInfo) {
    this.setState({ crash });
  }

  private handleCopy = async () => {
    const c = this.state.crash;
    if (!c) return;
    const text = [
      `[Корпы] КРАШ (${c.source || 'unknown'})`,
      `URL: ${window.location.href}`,
      `UA: ${navigator.userAgent}`,
      `Time: ${new Date().toISOString()}`,
      '',
      `Message: ${c.message}`,
      '',
      'Stack:',
      c.stack || '(нет stack)',
      '',
      'Component stack:',
      c.componentStack || '(нет componentStack)',
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // fallback через textarea (старые WebView без Clipboard API)
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
    // eslint-disable-next-line no-alert
    alert('Скопировано. Пришли разработчику.');
  };

  private handleReload = () => {
    try { window.location.reload(); } catch {}
  };

  render() {
    const c = this.state.crash;
    if (!c) return this.props.children;
    return (
      <div style={overlayStyle}>
        <div style={panelStyle}>
          <div style={titleStyle}>⚠ Корпы — КРАШ</div>
          <div style={metaStyle}>
            Источник: <b>{c.source || 'unknown'}</b>
            <br />
            URL: <code style={codeStyle}>{window.location.href}</code>
          </div>
          <div style={sectionTitleStyle}>Сообщение</div>
          <div style={messageStyle}>{c.message}</div>
          {c.stack && (
            <>
              <div style={sectionTitleStyle}>Stack</div>
              <pre style={preStyle}>{c.stack}</pre>
            </>
          )}
          {c.componentStack && (
            <>
              <div style={sectionTitleStyle}>Component stack (React)</div>
              <pre style={preStyle}>{c.componentStack}</pre>
            </>
          )}
          <div style={buttonsStyle}>
            <button onClick={this.handleCopy} style={btnPrimaryStyle}>
              📋 Скопировать
            </button>
            <button onClick={this.handleReload} style={btnSecondaryStyle}>
              ↻ Перезагрузить
            </button>
          </div>
        </div>
      </div>
    );
  }
}

// ── Inline-стили чтобы overlay работал даже если global.css сломан ──
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(20, 0, 0, 0.95)',
  zIndex: 999999,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '16px',
  overflowY: 'auto',
  fontFamily: 'monospace',
  color: '#ffd0d0',
};
const panelStyle: React.CSSProperties = {
  maxWidth: '720px',
  width: '100%',
  background: '#1a0606',
  border: '2px solid #ff4444',
  borderRadius: '8px',
  padding: '16px',
  boxShadow: '0 0 24px rgba(255, 68, 68, 0.4)',
};
const titleStyle: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: 'bold',
  color: '#ff4444',
  marginBottom: '12px',
};
const metaStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#ffaaaa',
  marginBottom: '12px',
  lineHeight: 1.4,
  wordBreak: 'break-all',
};
const sectionTitleStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 'bold',
  color: '#ff8888',
  marginTop: '12px',
  marginBottom: '4px',
};
const messageStyle: React.CSSProperties = {
  background: '#000',
  padding: '8px',
  borderRadius: '4px',
  fontSize: '13px',
  color: '#ffd0d0',
  wordBreak: 'break-word',
};
const preStyle: React.CSSProperties = {
  background: '#000',
  padding: '8px',
  borderRadius: '4px',
  fontSize: '11px',
  color: '#ffcccc',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: '260px',
  overflowY: 'auto',
  margin: 0,
};
const codeStyle: React.CSSProperties = {
  background: '#000',
  padding: '1px 4px',
  borderRadius: '3px',
  color: '#ffe0e0',
};
const buttonsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  marginTop: '16px',
};
const btnPrimaryStyle: React.CSSProperties = {
  flex: 1,
  padding: '12px',
  background: '#ff4444',
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  fontWeight: 'bold',
  cursor: 'pointer',
  fontFamily: 'monospace',
};
const btnSecondaryStyle: React.CSSProperties = {
  flex: 1,
  padding: '12px',
  background: '#330000',
  color: '#ffaaaa',
  border: '1px solid #ff4444',
  borderRadius: '4px',
  cursor: 'pointer',
  fontFamily: 'monospace',
};
